import type { ComponentProps } from "react";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/utils";
import "./markdown-editor.css";

type PreviewProps = ComponentProps<typeof MDEditor.Markdown>;

// Content is user-authored, so sanitize with GitHub's schema. Legacy
// descriptions embed pasted images as data: URLs; allow those on src only.
const SANITIZE_SCHEMA = {
	...defaultSchema,
	protocols: {
		...defaultSchema.protocols,
		src: [...(defaultSchema.protocols?.src ?? []), "data"],
	},
};
const REHYPE_PLUGINS: PreviewProps["rehypePlugins"] = [
	[rehypeSanitize, SANITIZE_SCHEMA],
];
// Links to this href render as a mention chip; see linkMentions() in comments.
export const MENTION_HREF = "#mention";

const COMPONENTS: PreviewProps["components"] = {
	// External links open in a new tab.
	a: ({ href, title, children }) => {
		if (href === MENTION_HREF) {
			return (
				<span className="rounded bg-primary/10 px-0.5 font-medium text-primary">
					{children}
				</span>
			);
		}
		const external = /^https?:/.test(href ?? "");
		return (
			<a
				href={href}
				title={title}
				target={external ? "_blank" : undefined}
				rel={external ? "noopener noreferrer" : undefined}
			>
				{children}
			</a>
		);
	},
};

export function MarkdownPreview({
	source,
	className,
}: {
	source: string;
	className?: string;
}) {
	const { resolvedTheme } = useTheme();
	return (
		<MDEditor.Markdown
			source={source}
			rehypePlugins={REHYPE_PLUGINS}
			components={COMPONENTS}
			disableCopy
			className={cn("md-preview", className)}
			wrapperElement={{ "data-color-mode": resolvedTheme }}
		/>
	);
}
